import * as React from "react";
import { cn } from "../lib/cn";

/**
 * ShimmerButton — hero CTA with a conic spark orbiting inside the border and
 * a soft inner highlight that deepens on hover/press. Ported from Creator
 * Studio v2 (`src/components/ui/shimmer-button.tsx`) and re-themed: fill and
 * spark default to the consumer's `--primary` / `--primary-foreground`
 * tokens instead of hardcoded navy + white, so each studio keeps its theme.
 *
 * Use sparingly — one per surface. For everyday actions reach for `<Button>`;
 * this is the "publish", "upgrade", "create my studio" moment.
 *
 * Press: in the Onyx "offset key" language this is a SIGNATURE-animated control,
 * so the spark is preserved untouched and it takes the quiet press tier — a 1px
 * press-DOWN on `:active` with the shared springy overshoot bounce (no notch, no
 * travel block: the silhouette is a pill by design and the spark mask + inner
 * backdrop are built on its `border-radius`, which a notch `clip-path` would
 * fight). This replaces the old uniform `scale(0.98)` so it presses like the
 * rest of the lib; reduced-motion users still get a static button.
 *
 * Anatomy notes:
 * - Inline CSS vars are namespaced `--sb-*` so they never collide with the
 *   global `--radius` token (the upstream source leaked one).
 * - The spark animates via the preset's `animate-shimmer-slide` /
 *   `animate-spin-around` utilities, which the preset's reduced-motion
 *   backstop disables globally — reduced-motion users get a static button.
 *
 * @example
 *   <ShimmerButton>Publish my studio</ShimmerButton>
 *   <ShimmerButton background="hsl(var(--accent))">Upgrade</ShimmerButton>
 */
export interface ShimmerButtonProps extends React.ButtonHTMLAttributes<HTMLButtonElement> {
  /** Spark colour. Any CSS colour. Default `hsl(var(--primary-foreground))`. */
  shimmerColor?: string;
  /** Border channel the spark travels in. CSS length. Default `0.05em`. */
  shimmerSize?: string;
  /** Outer radius. CSS length. Defaults to the brand pill (`9999px`). */
  borderRadius?: string;
  /** Duration of one spark pass. CSS time. Default `3s`. */
  shimmerDuration?: string;
  /** Button fill. Any CSS colour. Default `hsl(var(--primary))`. */
  background?: string;
}

export const ShimmerButton = React.forwardRef<HTMLButtonElement, ShimmerButtonProps>(
  (
    {
      shimmerColor = "hsl(var(--primary-foreground))",
      shimmerSize = "0.05em",
      shimmerDuration = "3s",
      borderRadius = "9999px",
      background = "hsl(var(--primary))",
      className,
      children,
      ...props
    },
    ref,
  ) => {
    return (
      <button
        ref={ref}
        style={
          {
            "--sb-speed": shimmerDuration,
            "--sb-color": shimmerColor,
            "--sb-radius": borderRadius,
            "--sb-cut": shimmerSize,
            "--sb-bg": background,
            borderRadius: "var(--sb-radius)",
          } as React.CSSProperties
        }
        className={cn(
          "group relative z-0 flex cursor-pointer items-center justify-center gap-2 overflow-hidden whitespace-nowrap px-5 py-2.5 text-sm font-medium text-primary-foreground [background:var(--sb-bg)]",
          // Onyx press: 1px press-DOWN on the shared springy bounce (the quiet
          // tier, since the spark is the signature here). Replaces the old
          // uniform scale-tap so it presses like the rest of the lib.
          "transform-gpu transition-transform duration-150 ease-[cubic-bezier(0.34,1.45,0.6,1)] active:translate-y-px motion-reduce:transition-none motion-reduce:active:translate-y-0",
          "ring-offset-background focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2",
          "disabled:pointer-events-none disabled:opacity-50",
          className,
        )}
        {...props}
      >
        {/* spark container */}
        <div className="absolute inset-0 -z-30 overflow-visible blur-[2px] [container-type:size]">
          {/* spark */}
          <div className="absolute inset-0 h-[100cqh] animate-shimmer-slide [aspect-ratio:1] [border-radius:0] [mask:none]">
            {/* rotating conic gradient */}
            <div className="absolute inset-[-100%] w-auto rotate-0 animate-spin-around [background:conic-gradient(from_calc(270deg-(var(--sb-spread)*0.5)),transparent_0,var(--sb-color)_var(--sb-spread),transparent_var(--sb-spread))] [translate:0_0] [--sb-spread:90deg]" />
          </div>
        </div>

        <span className="relative z-10 inline-flex items-center gap-2">{children}</span>

        {/* inner highlight */}
        <div
          className={cn(
            "absolute inset-0 size-full",
            "rounded-[inherit] shadow-[inset_0_-8px_10px_hsl(var(--primary-foreground)/0.12)]",
            "transform-gpu transition-all duration-320 ease-smooth-out",
            "group-hover:shadow-[inset_0_-6px_10px_hsl(var(--primary-foreground)/0.25)]",
            "group-active:shadow-[inset_0_-10px_10px_hsl(var(--primary-foreground)/0.25)]",
          )}
        />

        {/* backdrop — covers the spark except in the --sb-cut border channel */}
        <div
          style={{
            borderRadius: "calc(var(--sb-radius) - var(--sb-cut))",
            inset: "var(--sb-cut)",
          }}
          className="absolute -z-10 [background:var(--sb-bg)]"
        />
      </button>
    );
  },
);
ShimmerButton.displayName = "ShimmerButton";
